/**
 * 수집 캘린더 + ops_gap (RUNBOOK P4 위젯 5).
 *
 * **지표의 신뢰 근거이자 자기 감시 수단이다.** 변경 피드는 소급 조회가 불가능해서
 * (CLAUDE.md 절대원칙 4) "언제 못 봤는가"가 곧 "무엇을 영원히 모르는가"다.
 */

import { useQuery } from '@tanstack/react-query'

import { ChartFrame } from '@/components/ChartFrame'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import type { CalendarDay, Envelope, OpsGap } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import './CollectionCalendar.css'

interface Data {
  days: CalendarDay[]
  gaps: OpsGap[]
  n_polls: number
  success_rate: number | null
}

/** 9분 주기 기준 하루 기대 poll 수 — 부분 수집을 눈에 보이게 하는 기준선 */
const EXPECTED_PER_DAY = 160

export function CollectionCalendar() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['quality/calendar', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<Data>('/quality/calendar', toQueryFilters(filters), {}, signal),
    staleTime: 60 * 1000,
  })

  const env = q.data as Envelope<Data> | undefined
  const days = env?.data.days ?? []
  const gaps = env?.data.gaps ?? []
  const feedDays = days.filter((d) => d.op === 'feed')
  const successRate = env?.data.success_rate ?? null

  return (
    <ChartFrame
      title="수집 캘린더"
      question="언제 못 봤나 — 놓친 시간은 되돌릴 수 없다"
      meta={env?.meta}
      hideCoverage
      headline={
        <Headline
          value={successRate == null ? '—' : `${(successRate * 100).toFixed(2)}%`}
          unit={gaps.length > 0 ? `성공 · 공백 ${gaps.length}건` : '성공 · 공백 없음'}
          tone={gaps.length > 0 ? 'down' : 'up'}
        />
      }
      reading="날마다 수집기가 몇 번 성공했는지를 칸 하나로 그렸습니다. 변경 피드는 지나간 시각을 다시 조회할 수 없으므로, 색이 옅은 날은 그만큼 영구히 모르는 시간이 있다는 뜻입니다."
      encoding={[
        {
          role: '칸 하나',
          what: 'KST 기준 하루',
          detail: '다른 모든 일 집계와 같은 기준입니다 (UTC 가 아닙니다).',
        },
        {
          role: '색 진하기',
          what: `그날 수집 횟수 ÷ 기대치 ${EXPECTED_PER_DAY}회`,
          detail:
            '9분 주기로 하루 160회가 기대치입니다. 진할수록 촘촘히 봤다는 뜻이고, 옅은 칸은 그만큼 못 본 시간이 있습니다.',
        },
        {
          role: '칸 아래 숫자',
          what: '그날 실제 poll 횟수',
          detail: '오늘과 수집 개시일은 하루가 온전하지 않아 자연히 적습니다.',
        },
        {
          role: '해칭',
          what: '관측 공백 구간',
          detail: '기대 주기보다 오래 비어 있던 구간입니다. 이 구간과 겹친 상태 구간에는 GAP_SUSPECT 플래그가 붙습니다.',
        },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={days.length === 0}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '놓친 시간은 되돌릴 수 없다.',
          text: '변경 피드는 소급 조회가 불가능합니다. 수집이 멈춘 동안의 상태 전이는 영구히 알 수 없고, 나중에 채울 방법이 없습니다.',
        },
        {
          label: '공백은 지표에서 걸러낼 수 있다.',
          text: '공백과 겹친 구간에 GAP_SUSPECT 플래그가 붙어, 민감도 분석에서 포함/제외를 바꿔 볼 수 있습니다 (§8.5).',
        },
        {
          label: '스냅샷이 일부를 복구한다.',
          text: '하루 한 번의 전량 스냅샷에 statUpdDt 가 있어 마지막 전이 시각은 되살릴 수 있습니다. 복구되지 않는 것은 공백 안에서 두 번 이상 바뀐 중간 상태입니다 (§4.4).',
        },
      ]}
    >
      <p className="cal__summary">
        성공률{' '}
        <strong className="num">
          {successRate == null ? '—' : `${(successRate * 100).toFixed(2)}%`}
        </strong>{' '}
        · 총 poll <span className="num">{(env?.data.n_polls ?? 0).toLocaleString('ko-KR')}</span>회
        {gaps.length > 0 && (
          <>
            {' '}
            · 공백 <span className="num cal__gapcount">{gaps.length}</span>건
          </>
        )}
      </p>

      <ul className="cal">
        {feedDays.map((d) => {
          const ok = d.n_success + d.n_partial
          const ratio = Math.min(1, ok / EXPECTED_PER_DAY)
          const bad = d.n_failed + d.n_blocked
          return (
            <li key={`${d.date_kst}-${d.op}`} className="cal__day">
              <span className="cal__date num">{d.date_kst.slice(5)}</span>
              <span
                className={`cal__cell${bad > 0 ? ' cal__cell--bad' : ''}${
                  ratio < 0.9 ? ' cal__cell--partial' : ''
                }`}
                style={{ opacity: 0.25 + ratio * 0.75 }}
                title={`${d.date_kst} · 성공 ${ok} / 기대 ${EXPECTED_PER_DAY} · 실패 ${d.n_failed} · 차단 ${d.n_blocked}`}
              />
              <span className="cal__n num">{ok}</span>
            </li>
          )
        })}
      </ul>

      {gaps.length > 0 && (
        <details className="cal__gaps">
          <summary>
            수집 공백 <span className="num">{gaps.length}</span>건 —{' '}
            <span className="num">
              {gaps.reduce((s, g) => s + g.minutes, 0).toFixed(0)}
            </span>
            분
          </summary>
          <table>
            <thead>
              <tr>
                <th scope="col">시작</th>
                <th scope="col">끝</th>
                <th scope="col" className="n">
                  분
                </th>
                <th scope="col">원인</th>
              </tr>
            </thead>
            <tbody>
              {gaps.slice(0, 30).map((g) => (
                <tr key={`${g.start_ts}-${g.end_ts}`}>
                  <td className="num">{g.start_ts.slice(0, 19).replace('T', ' ')}</td>
                  <td className="num">{g.end_ts.slice(0, 19).replace('T', ' ')}</td>
                  <td className="n">{g.minutes.toFixed(1)}</td>
                  <td>{g.cause}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </ChartFrame>
  )
}
